/**
 * NextJS-specific Supabase client configuration
 * Uses cross-domain storage adapter for session sharing
 */

import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import {
  CrossDomainStorage,
  createCrossDomainStorage,
} from "@/lib/utils/crossDomainStorage";
import { getDomainInfo } from "@/lib/utils/domainDetection";

const SUPABASE_URL =
  process.env.NEXT_PUBLIC_SUPABASE_URL ||
  "https://rrmhxzaxghiovfqqggfv.supabase.co";
const SUPABASE_PUBLISHABLE_KEY =
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJybWh4emF4Z2hpb3ZmcXFnZ2Z2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDE0NDU3NTEsImV4cCI6MjA1NzAyMTc1MX0.yckUWhupNFQf3pEXoDtz0NFK4BmRhuBga2GCAAKlrDg";

/**
 * Create cross-domain storage adapter for Supabase
 */
function createCrossDomainStorageAdapter(): CrossDomainStorage {
  const domainInfo = getDomainInfo();

  return createCrossDomainStorage({
    storageKey: "SUPABASE_SESSION",
    cookieDomain: domainInfo.cookieDomain,
    enablePostMessage: domainInfo.isDevelopment,
    enableDebug: process.env.NODE_ENV === "development",
  });
}

/**
 * Create Supabase client with cross-domain storage
 */
function createSupabaseClient() {
  const storageAdapter = createCrossDomainStorageAdapter();

  return createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      storage: storageAdapter,
      storageKey: "SUPABASE_SESSION",
      detectSessionInUrl: true,
      flowType: "pkce",
    },
    global: {
      headers: {
        "X-Client-Info": "nextjs-cross-domain",
      },
    },
  });
}

/**
 * Create server-side Supabase client
 */
function createServerSupabaseClient() {
  return createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        "X-Client-Info": "nextjs-server",
      },
    },
  });
}

/**
 * Client-side Supabase instance with cross-domain storage
 */
export const supabase = createSupabaseClient();

/**
 * Server-side Supabase instance
 */
export const supabaseServer = createServerSupabaseClient();

/**
 * Get Supabase client for current environment
 */
export function getSupabaseClient() {
  if (typeof window === "undefined") {
    return supabaseServer;
  }
  return supabase;
}

/**
 * Initialize cross-domain session sync
 */
export function initializeCrossDomainSession() {
  if (typeof window === "undefined") return;

  const domainInfo = getDomainInfo();

  // Only initialize in development or production environments
  if (!domainInfo.isDevelopment && !domainInfo.isProduction) {
    return;
  }

  // Listen for auth state changes
  supabase.auth.onAuthStateChange((event, session) => {
    console.log(
      "[SupabaseClient] Auth state changed:",
      event,
      session?.user?.id
    );

    // Handle session changes for cross-domain sync
    switch (event) {
      case "SIGNED_IN":
        if (session) {
          // Session will be automatically stored by the cross-domain storage adapter
          console.log("[SupabaseClient] User signed in, session synced");
        }
        break;
      case "SIGNED_OUT":
        console.log("[SupabaseClient] User signed out, session cleared");
        break;
      case "TOKEN_REFRESHED":
        if (session) {
          console.log("[SupabaseClient] Token refreshed, session updated");
        }
        break;
    }
  });
}

/**
 * Get current session with cross-domain support
 */
export async function getCurrentSession() {
  const client = getSupabaseClient();
  const {
    data: { session },
    error,
  } = await client.auth.getSession();

  if (error) {
    console.error("[SupabaseClient] Error getting session:", error);
    return null;
  }

  return session;
}

/**
 * Get current user with cross-domain support
 */
export async function getCurrentUser() {
  const session = await getCurrentSession();
  return session?.user || null;
}

/**
 * Sign out with cross-domain session clearing
 */
export async function signOut() {
  const client = getSupabaseClient();
  const { error } = await client.auth.signOut();

  if (error) {
    console.error("[SupabaseClient] Error signing out:", error);
    return { error };
  }

  return { error: null };
}

/**
 * Refresh session with cross-domain sync
 */
export async function refreshSession() {
  const client = getSupabaseClient();
  const { data, error } = await client.auth.refreshSession();

  if (error) {
    console.error("[SupabaseClient] Error refreshing session:", error);
    return { data: null, error };
  }

  return { data, error: null };
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const session = await getCurrentSession();
  return !!session;
}

/**
 * Get session from cross-domain storage
 */
export function getSessionFromStorage() {
  if (typeof window === "undefined") return null;

  const storageAdapter = createCrossDomainStorageAdapter();
  return storageAdapter.getSessionData();
}

/**
 * Set session in cross-domain storage
 */
export function setSessionInStorage(sessionData: any) {
  if (typeof window === "undefined") return;

  const storageAdapter = createCrossDomainStorageAdapter();
  storageAdapter.setSessionData(sessionData);
}

/**
 * Clear session from cross-domain storage
 */
export function clearSessionFromStorage() {
  if (typeof window === "undefined") return;

  const storageAdapter = createCrossDomainStorageAdapter();
  storageAdapter.clearSession();
}

/**
 * Validate session from cross-domain storage
 */
export function validateSessionFromStorage(): boolean {
  if (typeof window === "undefined") return false;

  const storageAdapter = createCrossDomainStorageAdapter();
  return storageAdapter.isSessionValid();
}

/**
 * Initialize cross-domain session on client side
 */
if (typeof window !== "undefined") {
  // Initialize cross-domain session sync
  initializeCrossDomainSession();

  // Check for existing session on page load
  const existingSession = getSessionFromStorage();
  if (existingSession && validateSessionFromStorage()) {
    console.log("[SupabaseClient] Found valid cross-domain session");
  }
}
